import {
    dehydrate,
    focusManager,
    hydrate,
    QueryClient,
    VueQueryPlugin,
    type DehydratedState,
} from '@tanstack/vue-query';
import {nextTick, type Plugin} from 'vue';
import {refreshWallClock} from '@/shared/helpers/wallClock';

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // 10 minutes - can be overriden for each individual query
            staleTime: 10 * 60 * 1000,
            retry: false,
        },
    },
});

export const tanStackPlugin = {
    install(app) {
        addQueryPersistence(queryClient);
        refreshWallClockBeforeFocusRefetch();
        app.use(VueQueryPlugin, {queryClient});
    },
} satisfies Plugin;

// Query keys derived from the clock (e.g. selected dates after a day change) must update before the
// focus refetch; vue-query applies new options only on the next tick.
function refreshWallClockBeforeFocusRefetch() {
    focusManager.setEventListener(handleFocus => {
        async function onVisibilityChange() {
            refreshWallClock();
            await nextTick();
            handleFocus();
        }

        window.addEventListener('visibilitychange', onVisibilityChange, false);

        return () => window.removeEventListener('visibilitychange', onVisibilityChange);
    });
}

function addQueryPersistence(queryClient: QueryClient) {
    try {
        const cachedData = localStorage.getItem('vue-query-cache');

        if (cachedData) {
            hydrate(queryClient, JSON.parse(cachedData));
        }
    } catch {
        // ignore errors
    }

    queryClient.getQueryCache().subscribe(event => {
        if (!shouldPersistQuery(event.query)) {
            return;
        }
        const data = dehydrate(queryClient);
        const filteredData = {
            queries: data.queries.filter(shouldPersistQuery),
            mutations: data.mutations.filter(shouldPersistQuery),
        } satisfies DehydratedState;
        localStorage.setItem('vue-query-cache', JSON.stringify(filteredData));
    });
}

function shouldPersistQuery(query: {meta?: Record<string, unknown>}): boolean {
    return !!query.meta?.persist;
}
